import type { WaitlistCaptureSurface } from '../types/waitlist';

/**
 * The one fact every waitlist surface shares: this browser has joined.
 *
 * The shared form writes it after any save, and anything that should stop
 * asking (the popup) reads it or listens for it. Kept out of the popup so the
 * form does not depend on the popup's rules, and out of waitlistService so
 * reading it does not boot Firebase.
 */
export const WAITLIST_JOINED_KEY = 'melio_waitlist_joined';

export interface JoinedStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

type JoinedListener = (surface: WaitlistCaptureSurface) => void;
const listeners = new Set<JoinedListener>();

function defaultStorage(): JoinedStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** True once joined. Throws when storage cannot be read, so callers choose how to fail. */
export function hasJoinedWaitlist(storage: JoinedStorage): boolean {
  return storage.getItem(WAITLIST_JOINED_KEY) !== null;
}

/** Remembers the join and tells anything listening which surface it came from. */
export function markWaitlistJoined(
  surface: WaitlistCaptureSurface,
  storage: JoinedStorage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(WAITLIST_JOINED_KEY, '1');
  } catch {
    // Storage blocked: listeners on this page still hear about it below.
  }
  listeners.forEach((listener) => {
    try {
      listener(surface);
    } catch {
      // One listener failing must not get in the way of a signup.
    }
  });
}

/** Calls `listener` on every later join in this page. Returns the unsubscribe. */
export function onWaitlistJoined(listener: JoinedListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
